/**
 * BAB Report — admin tab: who may open BOOKING-level detail, per source company.
 *
 * Level 3 serves individual postings whose descriptions name people ("Manpower, Goua Bi
 * Balman 03.2026", "Helsana, Taggeld … ERJ"), so it is granted explicitly rather than
 * implied by page access. Level 2 (company/BU/account totals) stays open to everyone who
 * can see the page — this tab does not touch it.
 *
 * The checkboxes are an affordance; the enforcement is server-side
 * (api/src/functions/bab-report/detail-scope.ts).
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Check, Loader2, Save, ShieldAlert, Search } from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { useToast } from '../ui/Toast';
import { formatCompanyLabel } from '../../utils/buTheme';
import type { BabDetailAccessMatrix, BabDetailAccessUser } from '../../types/babReport';

export function BabDetailAccessTab() {
    const { toast } = useToast();
    const [matrix, setMatrix] = useState<BabDetailAccessMatrix | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [savingEmail, setSavingEmail] = useState<string | null>(null);
    /** Pending edits per user, keyed by e-mail. Absent = row is clean. */
    const [draft, setDraft] = useState<Record<string, string[]>>({});

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await babReportApi.getDetailAccess();
            setMatrix(data);
            setDraft({});
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load detail access');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const granted = (u: BabDetailAccessUser): string[] => draft[u.email] ?? u.sourceCompanies;

    const isDirty = (u: BabDetailAccessUser): boolean => {
        const d = draft[u.email];
        if (!d) return false;
        const a = [...d].sort().join(',');
        const b = [...u.sourceCompanies].sort().join(',');
        return a !== b;
    };

    const toggle = (u: BabDetailAccessUser, code: string) => {
        const current = granted(u);
        const next = current.includes(code) ? current.filter(c => c !== code) : [...current, code];
        setDraft(d => ({ ...d, [u.email]: next }));
    };

    const setAll = (u: BabDetailAccessUser, all: boolean) => {
        setDraft(d => ({ ...d, [u.email]: all ? (matrix?.companies.map(c => c.code) ?? []) : [] }));
    };

    /**
     * One PUT per user with the whole set. Saving per checkbox would be ten requests for a
     * full grant and could leave half a scope applied if one failed.
     */
    const save = async (u: BabDetailAccessUser) => {
        const next = granted(u);
        setSavingEmail(u.email);
        try {
            await babReportApi.saveDetailAccess(u.email, next);
            setMatrix(m => m && {
                ...m,
                users: m.users.map(x => x.email === u.email ? { ...x, sourceCompanies: [...next].sort() } : x),
            });
            setDraft(d => { const { [u.email]: _drop, ...rest } = d; return rest; });
            toast(`Booking detail for ${u.displayName || u.email} saved.`, 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to save', 'error');
        } finally {
            setSavingEmail(null);
        }
    };

    const visibleUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q || !matrix) return matrix?.users ?? [];
        return matrix.users.filter(u =>
            u.email.toLowerCase().includes(q) || u.displayName.toLowerCase().includes(q));
    }, [matrix, search]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }
    if (error) {
        return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>;
    }
    if (!matrix) return null;

    return (
        <div className="space-y-4">
            {/* The consequence of company granularity, stated where the decision is made. */}
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                    <p className="font-medium">Granting a company grants every individual posting of that company.</p>
                    <p className="mt-0.5 text-xs">
                        That includes personnel bookings which name people, on any line of the report.
                        Company and account totals stay visible to everyone who can view the BAB Report —
                        this setting only governs the individual bookings behind an account.
                        Admins are unrestricted and are not listed here.
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700">Booking-detail access</h3>
                        <p className="text-xs text-gray-500">
                            Users with view access to the BAB Report. No ticked company means no booking detail at all.
                        </p>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search name or e-mail"
                            className="w-56 rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm"
                        />
                    </div>
                </div>

                {matrix.users.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">
                        No user has view access to the BAB Report yet. Grant it under{' '}
                        <span className="font-medium">Settings → Permissions</span>, then configure booking-detail
                        access here.
                    </p>
                ) : visibleUsers.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-gray-500">No user matches that search.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm border-collapse [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="px-4 py-2 text-left font-medium text-gray-600">User</th>
                                    {matrix.companies.map(c => (
                                        <th key={c.code} className="px-2 py-2 text-center font-medium uppercase text-gray-600" title={c.name}>
                                            {c.code}
                                        </th>
                                    ))}
                                    <th className="px-3 py-2 text-right font-medium text-gray-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {visibleUsers.map(u => {
                                    const current = granted(u);
                                    const dirty = isDirty(u);
                                    return (
                                        <tr key={u.email} className={dirty ? 'bg-blue-50/40' : undefined}>
                                            <td className="px-4 py-2">
                                                <div className="font-medium text-gray-900">{u.displayName || u.email}</div>
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                    <span>{u.email}</span>
                                                    {u.canEdit && (
                                                        <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                                                            can comment
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            {matrix.companies.map(c => {
                                                const on = current.includes(c.code);
                                                return (
                                                    <td key={c.code} className="px-2 py-2 text-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggle(u, c.code)}
                                                            title={`${on ? 'Revoke' : 'Grant'} booking detail for ${formatCompanyLabel(c.code)}`}
                                                            className={`h-6 w-6 rounded border transition-colors ${on
                                                                ? 'border-blue-500 bg-blue-500 text-white'
                                                                : 'border-gray-300 bg-white hover:border-gray-400'}`}
                                                        >
                                                            {on && <Check className="mx-auto h-4 w-4" />}
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                            <td className="px-3 py-2 text-right">
                                                <div className="inline-flex items-center gap-2">
                                                    <button onClick={() => setAll(u, true)} className="text-xs text-blue-600 hover:underline">All</button>
                                                    <button onClick={() => setAll(u, false)} className="text-xs text-blue-600 hover:underline">None</button>
                                                    {dirty && (
                                                        <button
                                                            onClick={() => void save(u)}
                                                            disabled={savingEmail === u.email}
                                                            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                                        >
                                                            {savingEmail === u.email
                                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                : <Save className="h-3.5 w-3.5" />} Save
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
